import { inject, Injectable, Optional } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, Subject, Subscription, switchMap } from 'rxjs';
import { JobTemplateModel } from '../../../models/job_template/job-template.model';
import { CompoApp } from '../../../services/CompoApp';
import { UntypedFormGroup } from '@angular/forms';
import { ModuleTranslatedDtoModel } from '../../../models/module/module-translated-dto.model';
import { JobRoutineFormModel } from '../create-jobroutine-design-b/forms/job-routine-form.model';
import { filter, map, startWith } from 'rxjs/operators';
import { JobRoutineFilesFormModel } from '../create-jobroutine-design-b/forms/job-routine-files-form.model';
import { UnsubscribeBaseServiceModel } from '../../../services/client/UnsubscribeBaseService.model';
import { DocumentGroupReferenceType } from '../../../enums/document/DocumentGroupReferenceType';
import { EditModuleDesignBClientService } from '../edit-module-design-b/edit-module-design-b-client.service';
import { ModuleFormModel } from '../create-module-design-b/forms/module-form.model';
import { ObjectDetailModel } from '../../../models/component/page/detail/object-detail.model';
import { JobRoutineService } from '../../../services/job-routine.service';
import { ObjectRequestService } from '../../../services/request/object-request.service';

@Injectable()
export class EditJobRoutineClientService extends UnsubscribeBaseServiceModel {

  readonly #jobRoutineTemplate = inject(JobRoutineService);
  readonly #objectRequestService = inject(ObjectRequestService);

  readonly CATEGORY = 'category';
  readonly DESCRIPTION = 'description';
  readonly EXECUTION_LOCATION_EXTERN = 'executionLocationExtern';
  readonly ORGANIZATION = 'organization';
  readonly SERVICE_PROVIDER = 'serviceProvider';
  readonly ASSIGNEE_TYPE = 'assigneeType';
  readonly ASSIGNED_USER = 'assignedUser';
  readonly ASSIGNED_WORK_GROUP = 'assignedWorkGroup';
  readonly JOB_BEGIN_STATUS = 'jobBeginStatus';

  isDialogVisible$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  currentJobTemplate$: BehaviorSubject<JobTemplateModel> = new BehaviorSubject<JobTemplateModel>(null);
  private currentObjectSubject$: BehaviorSubject<ObjectDetailModel> = new BehaviorSubject<ObjectDetailModel>(null);
  currentObject$: Observable<ObjectDetailModel> = this.currentObjectSubject$.asObservable();
  private currentModuleSubject$: BehaviorSubject<ModuleTranslatedDtoModel> = new BehaviorSubject<ModuleTranslatedDtoModel>(null);
  currentModule$: Observable<ModuleTranslatedDtoModel> = this.currentModuleSubject$.asObservable();

  hasChanges$: Subject<boolean> = new Subject<boolean>();

  dateFormat$: BehaviorSubject<string> = new BehaviorSubject<string>('DD, dd.mm.y');

  private assigneeOrgIdSubject$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  assigneeOrgId$: Observable<string> = this.assigneeOrgIdSubject$.asObservable();
  private serviceProviderIdSubject$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  serviceProviderId$: Observable<string> = this.serviceProviderIdSubject$.asObservable();
  public openReadOnlyView: boolean = false;

  //region form
  private formModel: JobRoutineFormModel;
  private editFormSubject$: BehaviorSubject<UntypedFormGroup> = new BehaviorSubject<UntypedFormGroup>(null);
  editForm$: Observable<UntypedFormGroup> = this.editFormSubject$.asObservable();
  editFormValueChanges$: Observable<any> = this.editForm$.pipe(
    filter(v => v !== null && v !== undefined),
    switchMap(form => form.valueChanges)
  );
  editInfoForm$: Observable<UntypedFormGroup> = this.editForm$.pipe(
    filter(v => v !== null && v !== undefined),
    map(form => form.get(JobRoutineFormModel.INFOS) as UntypedFormGroup)
  );
  editTimeForm$: Observable<UntypedFormGroup> = this.editForm$.pipe(
    filter(v => v !== null && v !== undefined),
    map(form => form.get(JobRoutineFormModel.TIME) as UntypedFormGroup)
  );
  editFilesForm$: Observable<UntypedFormGroup> = this.editForm$.pipe(
    filter(v => v !== null && v !== undefined),
    map(form => form.get(JobRoutineFormModel.FILES) as UntypedFormGroup)
  );
  editFilesFormDocumentCount$: Observable<number> = this.editFilesForm$.pipe(
    map(filesForm => filesForm.get(JobRoutineFilesFormModel.DOCUMENTS)),
    switchMap((documentsForm) => documentsForm.valueChanges),
    // tap((change) => console.dir(change)),
    map(docArray => docArray.length),
    startWith(0)
  );
  private isTemplateSubject$: BehaviorSubject<boolean> = new BehaviorSubject(null);
  public isTemplate$: Observable<boolean> = this.isTemplateSubject$.asObservable();
  //endregion form

  //region form validation
  isEditFormValid$: Observable<boolean> = combineLatest([this.editForm$, this.editFormValueChanges$])
    .pipe(map(([form, changes]) => form?.valid));
  isEditFormDisabled$: Observable<boolean> = combineLatest([this.editForm$, this.editFormValueChanges$])
    .pipe(map(([form, changes]) => form?.disabled));
  isEditFormInValid$: Observable<boolean> = this.isEditFormValid$.pipe(map(valid => !valid));
  //endregion form validation

  private subs: Subscription[] = [];

  constructor(
    private app: CompoApp,
    @Optional() public editModuleDesignBClientService: EditModuleDesignBClientService
  ) {
    super();
  }

  showDialog(): void {
    this.isDialogVisible$.next(true);
  }

  hideDialog(): void {
    this.isDialogVisible$.next(false);
  }

  cleanUp() {
    this.openReadOnlyView = false;
    this.editFormSubject$.next(null);
    this.currentJobTemplate$.next(null);
    this.isTemplateSubject$.next(null);
  }

  initJobTemplate(id: string, componentId: string, disabled = null): void {
    this.currentModuleSubject$.next(null);

    this.#objectRequestService.byId(componentId)
      .then(component => {
        this.currentObjectSubject$.next(component);
      });

    this.#jobRoutineTemplate.fetchJobRoutineById(id)
      .then(jobTemplate => {
        this.currentJobTemplate$.next(jobTemplate);
        this.assigneeOrgIdSubject$.next(jobTemplate.serviceProvider?.associatedOrg?.id || jobTemplate.ownerOrg?.id);
        this.serviceProviderIdSubject$.next(jobTemplate.serviceProvider?.id);
        //DLOUHY-11620 - comment out expiredDate - '!!jobTemplate?.expiredDate' -> 'false'
        const disable = disabled === null ? false : disabled;
        this.initForm(jobTemplate, disable);
      });

    this.isTemplateSubject$.next(false);
    this.showDialog();
  }

  initJobRoutineTemplateForActiveRelease(
    jobRoutineTemplateId: string,
    moduleId: string,
    disabled: boolean
  ): void {
    this.currentObjectSubject$.next(null);

    this.app.moduleService.fetchModuleById(moduleId)
      .then(module => {
        this.currentModuleSubject$.next(module);
      });

    this.app.jobRoutineTemplateService.getByIdForActiveVersion(jobRoutineTemplateId)
      .then(jobRoutineTemplate => {
        this.currentJobTemplate$.next(jobRoutineTemplate);
        this.assigneeOrgIdSubject$
          .next(jobRoutineTemplate?.serviceProvider?.associatedOrg?.id || jobRoutineTemplate?.ownerOrg?.id);
        this.initForm(jobRoutineTemplate, disabled || jobRoutineTemplate?.isHistoricalData);
      });

    this.isTemplateSubject$.next(true);
    this.showDialog();
  }

  initJobRoutineTemplateByModuleForReleaseNumber(
    jobRoutineTemplateId: string,
    module: ModuleTranslatedDtoModel,
    selectedVersion: number,
    disabled: boolean
  ): void {
    this.currentModuleSubject$.next(module);
    this.currentObjectSubject$.next(null);

    this.app.jobRoutineTemplateService
      .getByIdForVersion(jobRoutineTemplateId, selectedVersion)
      .then(jobRoutineTemplate => {
        this.currentJobTemplate$.next(jobRoutineTemplate);
        this.assigneeOrgIdSubject$
          .next(jobRoutineTemplate.serviceProvider?.associatedOrg?.id || jobRoutineTemplate.ownerOrg?.id);
        this.initForm(jobRoutineTemplate, disabled || jobRoutineTemplate.isHistoricalData);
      });

    this.isTemplateSubject$.next(true);
    this.showDialog();
  }

  checkJobTemplateStatus(): void {
    const currentJobTemplate: JobTemplateModel = this.currentJobTemplate$.getValue();

    if (!currentJobTemplate) {
      return;
    }

    this.#jobRoutineTemplate.fetchJobRoutineById(currentJobTemplate.id)
      .then(jobTemplate => {
        if (!!currentJobTemplate.expiredDate !== !!jobTemplate.expiredDate) {
          this.currentJobTemplate$.next(jobTemplate);
          this.initForm(jobTemplate, !!jobTemplate?.expiredDate);
          this.hasChanges$.next(true);
        }
      });
  }

  update(id: string): Promise<JobTemplateModel> {
    const updateView = this.formModel.createUpdateView(id);

    if (!updateView.jobTypeId) {
      updateView.jobTypeId = updateView.jobType;
      updateView.jobType = null;
    }
    if (this.isTemplateSubject$.getValue()) {
      updateView.moduleId = this.currentModuleSubject$.getValue().id;
      return this.app.jobRoutineTemplateService.update(id, updateView as any)
        .then(resp => {
          this.hasChanges$.next(true);
          return resp;
        });
    } else {
      updateView.documents.forEach(dg => dg.referenceType = DocumentGroupReferenceType.JOB_ROUTINE);
      return this.#jobRoutineTemplate.updateJobTemplate(updateView)
        .then(resp => {
          this.hasChanges$.next(true);
          return resp;
        });
    }
  }

  delete(id: string, isTemplate: boolean = false): Promise<void> {
    if (this.isTemplateSubject$.getValue() || isTemplate) {
      if (this.editModuleDesignBClientService) {
        const form = this.editModuleDesignBClientService
          .form$.getValue();
        const control = form
          .get(ModuleFormModel.JOB_ROUTINE_TEMPLATES_TO_DELETE);

        control
          .setValue([...control.value, id]);
        control.markAsDirty();
        this.editModuleDesignBClientService.form$.next(form);
      }
      return Promise.resolve();
    } else {
      return this.#jobRoutineTemplate.deleteJobTemplate(id);
    }
  }

  private initForm(jobTemplate: JobTemplateModel, disable: boolean): void {
    this.formModel = new JobRoutineFormModel(jobTemplate, this.app.data.getAccessMode());

    const formGroup = this.formModel.asFormGroup();
    if (disable || this.openReadOnlyView) {
      this.formModel.disableForm();
    }
    this.editFormSubject$.next(formGroup);

    this.serviceProviderIdSubject$.next(jobTemplate.serviceProvider?.id);

    //initialize documentCount
    if (this.formModel.files.documentsControl.value?.length > 0) {
      this.formModel.files.documentsControl.patchValue(this.formModel.files.documentsControl.value);
    } else {
      this.formModel.files.documentsControl.patchValue([]);
    }

    const isRoutine: boolean = !jobTemplate.componentId;
    this.setupValueChanges(isRoutine);
  }

  private setupValueChanges(isRoutine: boolean): void {
    if (!this.formModel) return;

    //clear subscriptions
    this.subs.forEach(s => s.unsubscribe());

    this.subs.push(this.formModel.infos.setupAssigneeTypeValueChanges());
    this.subs.push(this.formModel.infos.setupResponsibilityInternValueChanges(this.assigneeOrgIdSubject$));
    this.subs.push(this.formModel.infos.setupServiceProviderValueChanges(this.assigneeOrgIdSubject$, this.serviceProviderIdSubject$));
    this.subs.push(this.formModel.infos.setupOrganizationValueChanges(this.assigneeOrgIdSubject$));
    this.subs.push(this.formModel.infos.setupExecutionLocationValueChanges());
    this.subs.push(this.formModel.time.setupTimeStandStillValueChanges(this.unSubscribe$));
    this.subs.push(this.formModel.time.setupTimeIsIdenticalValueChanges(this.unSubscribe$));
  }
}
